import { findRenderer } from './blocks/registry';
import { FormAnswers, FormBlockInstance, FormDefinition } from './api-types';

/** The blocks that actually collect something, in order. CONTENT blocks are read-only. */
export const inputBlocksOf = (
  definition: FormDefinition | null
): FormBlockInstance[] =>
  (definition?.blocks ?? []).filter(
    block => findRenderer(block)?.kind === 'INPUT'
  );

/** Whether this block has an answer, decided by the block type rather than by shape guessing. */
export const isBlockAnswered = (
  block: FormBlockInstance,
  answers: FormAnswers
): boolean => {
  const renderer = findRenderer(block);
  if (!renderer || renderer.kind !== 'INPUT') {
    return true;
  }
  return renderer.isAnswered(answers[block.instanceId], block);
};

/** The required blocks still waiting for an answer, in the order they are asked. */
export const missingRequiredBlocksOf = (
  definition: FormDefinition | null,
  answers: FormAnswers
): FormBlockInstance[] =>
  inputBlocksOf(definition).filter(
    block => block.required && !isBlockAnswered(block, answers)
  );

/**
 * Where to open the form.
 *
 * The first question still missing an answer — which is where someone returning to a half-finished
 * form wants to be. When nothing is missing, the last block: they are reviewing before submitting,
 * not starting over.
 *
 * A FIRST open always starts at the beginning, though, even when the first block is not a question.
 * Jumping straight to question one would skip an introductory statement entirely — and an
 * explanation nobody is shown is the same as one that was never written. "Nothing answered yet" is
 * the honest signal for a first open: resume only means something once there is progress to resume.
 *
 * **OPTIONAL QUESTIONS COUNT AS MISSING.** This used to look for the first unanswered REQUIRED
 * block, which silently jumped over every optional question between here and there: answer question
 * one, reload, and land on question four having never been shown two or three. They were not
 * declined — they were never displayed. So they were never answered, never saved, and never reached
 * the operator, who had no way to tell an unasked question from a skipped one.
 *
 * That trades one annoyance for a data-loss bug, deliberately. Somebody who genuinely means to leave
 * an optional question blank and returns later now lands back on it and has to press Next again. A
 * question asked twice is a small cost; a question never asked at all is an answer nobody can
 * recover, and nobody knows to look for.
 *
 * `isBlockAnswered` reports CONTENT blocks as answered, so while anything is still outstanding you
 * land on the first outstanding QUESTION and never on prose you have already read.
 *
 * With nothing outstanding you land on the LAST block, and that one CAN be a statement — a form
 * ending in "check your answers, then send" resumes onto exactly that. Deliberate: the last block is
 * where Submit is, since the runner only offers it there. Resuming to the last INPUT block instead
 * would drop a returning respondent onto a question they have already answered, behind a Next they
 * have to press to reach the button they came back for.
 *
 * A REOPENED response is the exception to both of those. Everything in it is already answered, so the rule above would land on
 * the last block, which can be one the respondent may not change. They are here to redo the questions the operator released, so
 * with any question locked the form opens on the first question that is NOT locked.
 */
export const resumeIndexOf = (
  definition: FormDefinition | null,
  answers: FormAnswers,
  lockedBlockIds: ReadonlySet<string> = new Set()
): number => {
  const blocks = definition?.blocks ?? [];
  if (!blocks.length) {
    return 0;
  }
  if (lockedBlockIds.size > 0) {
    return Math.max(
      0,
      blocks.findIndex(
        block =>
          findRenderer(block)?.kind === 'INPUT' &&
          !lockedBlockIds.has(block.instanceId)
      )
    );
  }
  if (!Object.keys(answers).length) {
    return 0;
  }
  const firstMissing = blocks.findIndex(
    block => !isBlockAnswered(block, answers)
  );
  return firstMissing >= 0 ? firstMissing : blocks.length - 1;
};

/**
 * Share of the required questions answered, for the progress bar.
 *
 * Only required ones count, so leaving an optional question blank never makes a finished form look
 * unfinished. This mirrors what pro-core stores as the invitation's progress — the operator funnel
 * and the person filling it in should not disagree about how far along they are.
 */
export const progressPercentOf = (
  definition: FormDefinition | null,
  answers: FormAnswers
): number => {
  const required = inputBlocksOf(definition).filter(block => block.required);
  if (!required.length) {
    return Object.keys(answers).length ? 100 : 0;
  }
  const answered = required.filter(block =>
    isBlockAnswered(block, answers)
  ).length;
  return Math.round((100 * answered) / required.length);
};
